import React, { lazy, Suspense, useEffect } from 'react';

import { useAtomValue } from 'jotai';
import { Navigate } from 'react-router-dom';

import {
  ContractStepKeyT,
  furthestStepIndexAtom,
  LoadingCard,
  STEP_KEYS,
  useContractFlow,
  useRevealStepErrors,
} from '@consumer-frontend/components';

const STEP_COMPONENTS: Record<ContractStepKeyT, React.LazyExoticComponent<React.FC>> = {
  [STEP_KEYS.AUTH]: lazy(async () => {
    const { LocAuthPage } = await import('./pages/LocAuthPage');

    return { default: LocAuthPage };
  }),
  [STEP_KEYS.FUNDS]: lazy(async () => {
    const { LocFundsPage } = await import('./pages/LocFundsPage');

    return { default: LocFundsPage };
  }),
};

export const ContractStepRoute: React.FC = () => {
  const { current, currentIndex, steps } = useContractFlow();
  const furthestStepIndex = useAtomValue(furthestStepIndexAtom);

  // The customer typed their way past a step they have not completed.
  const isValidStep = Boolean(current);
  const isPastProgress = currentIndex > furthestStepIndex;
  const hasSkippedAhead = isValidStep && isPastProgress;
  const revealFirstStepErrors = useRevealStepErrors(steps[0]?.key);

  useEffect(() => {
    if (hasSkippedAhead) {
      revealFirstStepErrors();
    }
  }, [hasSkippedAhead, revealFirstStepErrors]);

  // Unknown or missing :step — send the customer to the first step.
  if (!current) {
    return steps.length ? <Navigate replace to={`/contract/${steps[0].key}`} /> : null;
  }

  if (hasSkippedAhead) {
    return <Navigate replace to={`/contract/${steps[0].key}`} />;
  }

  const StepComponent = STEP_COMPONENTS[current.key];

  return (
    <Suspense fallback={<LoadingCard />}>
      <StepComponent />
    </Suspense>
  );
};
